<template>
    <section id="play-alone" class="layout layout-scale overflow-hidden">
        <div class="relative" style="z-index: 1">
            <QuizHeader
                :title="stripHtmlTag(title)"
                :currentQuizIndex="currentQuizIndex"
                :questionCount="questionCount"
            />
            <bg-music-mute-btn
                v-if="question.question_type !== 'video' && question.question_type !== 'music'"
                class="mr-6 md:mr-8"
                :is-muted="isMuted"
                :is-spotify="isSoundQuestion"
            ></bg-music-mute-btn>
            <ClosePlayingButton :url="quiz.url" :embed="embedToken" />
        </div>

        <div class="content flex items-center animated fadeIn pt-3">
            <div class="mx-auto content-container w-full">
                <div
                    v-if="question.question_type !== 'puzzle'"
                    class="flex pt-0 sm:pt-6 items-center flex-col justify-center"
                >
                    <Equaliser v-if="isSoundQuestion" class="font-size-30" style="margin-bottom: 1rem" />
                    <div
                        v-if="question.image && question.image !== '' && question.question_type !== 'hotspot'"
                        :class="{
                            'bg-main-container border-style': true,
                            'has-video': question.question_type === 'video',
                            invisible: hideTitleImage
                        }"
                    >
                        <div
                            v-if="question.question_type !== 'video'"
                            :class="{
                                'bg-main bg-cover bg-image bg-center bg-norepeat border-3 mx-auto': true,
                                'w-48 sm:w-64 h-48 sm:h-64': isAllImageAnswer(question.answers),
                                'w-64 h-64': !isAllImageAnswer(question.answers),
                                'animate-reveal': question.question_type === 'reveal_image'
                            }"
                            :style="{ 'background-image': 'url(' + question.image + ')' }"
                        ></div>
                        <div v-if="question.question_type == 'video'" class="video-box h-64 border-3">
                            <video-player
                                ref="videoPlayer"
                                class="w-full h-full"
                                v-if="question.question_type == 'video'"
                                :video-meta="question"
                                :video-path="question.video"
                                :time-duration="question.video_time"
                                :is-embed="embedToken ? true : false"
                            >
                            </video-player>
                        </div>
                    </div>
                    <h1
                        id="question-title"
                        class="theme-question-text-color theme-question-shadow text-center mt-0 pt-2 mb-5 w-full has-text-balanced"
                        :class="{ 'mb-12': question.image === null && question.image === '', hidden: !question.title }"
                    >
                        {{ question.title ? question.title : 'Fallback Title' }}
                    </h1>

                    <!-- Fallback title is empty -->
                    <div v-if="!question.title" class="my-2"></div>
                    <!-- Fallback title is empty -->
                </div>
                <!-- START SELECT -->
                <div v-if="isMultiple" class="text-center">
                    <span
                        class="theme-question-text-color theme-question-shadow text-white font-size-16 inline-block mb-2"
                        >{{ $t('playalone_correct_count', { answersCount: question.answers_count }) }}</span
                    >
                </div>
                <div class="answer-box" :style="[question.question_type == 'hotspot' ? { padding: 0 } : {}]">
                    <ChoiceAnswer
                        v-if="
                            ['basic', 'music', 'reveal_image', 'scale_image', 'video'].indexOf(question.question_type) >
                                -1 || !question.question_type
                        "
                        :question="question"
                        :isAllImageAnswer="isAllImageAnswer(question.answers)"
                        :selectedChoice="selectedChoice"
                        :isMultiple="isMultiple"
                        :isPlayAlone="true"
                        @selectAnswer="answerIndex => handleClickSelectAnswer(answerIndex)"
                    />
                    <TypingAnswer
                        v-if="question.question_type == 'typing_question'"
                        :question="question"
                        :isPlayAlone="true"
                        @value="submitTyping"
                    />
                    <SortingAnswer
                        v-if="question.question_type == 'sorting_list'"
                        :question="question"
                        @value="submitTyping"
                    />
                    <HotspotAnswer
                        v-if="question.question_type == 'hotspot'"
                        ref="hotspot-container"
                        :question="question"
                        :isMultiple="isMultiple"
                        @selectAnswer="answerIndex => handleClickSelectAnswer(answerIndex)"
                    />
                </div>
                <!-- END SELECT -->

                <!-- PUZZLE -->
                <div v-if="question.question_type === 'puzzle'" ref="puzzle-container" class="puzzle-container">
                    <div class="puzzle-backdrop">
                        <div class="circle-box">
                            <div class="circle-bg hide"></div>
                            <i id="check-icon" class="fas hide theme-ui-text-color"></i>
                            <i id="x-icon" class="fas fa-times-circle hide theme-ui-text-color"></i>
                        </div>
                    </div>
                </div>
                <!-- END PUZZLE -->

                <!-- SCRAMBLE -->
                <div v-if="question.question_type == 'scramble'">
                    <template
                        v-if="typeof question.custom_message !== 'undefined' && question.custom_message.length > 0"
                    >
                        <div
                            class="theme-question-text-color theme-question-shadow text-white text-center font-size-16 mb-2"
                        >
                            {{ question.custom_message }}
                        </div>
                    </template>
                    <p
                        v-else
                        class="theme-question-text-color theme-question-shadow text-center text-white mb-2 font-size-16"
                    >
                        <i class="theme-question-shadow fas fa-arrows-alt"></i>
                        {{ $t('sorting_dragdrop_correct_answer') }}
                    </p>
                    <Scramble :question="question" :isPlayAlone="true" @value="submitTyping" />
                </div>
                <!-- END SCRAMBLE -->
            </div>
        </div>
        <div class="footer"></div>
    </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import popup from '@/lib/popup.js'
import helper from '@/lib/helper.js'
import ResizeScale from '@/lib/resize-scale.js'
import gsap from 'gsap'
import preloadSongMixin from '@/mixins/preload-song'
import Equaliser from '@/components/Equaliser.vue'
import { isLoggedIn } from '@/lib/user.js'
import ChoiceAnswer from '@/components/ChoiceAnswer.vue'
import TypingAnswer from '@/components/TypingAnswer.vue'
import Scramble from '@/components/Scramble.vue'
import Puzzle from '@/lib/puzzle.js'
import SortingAnswer from '@/components/SortingAnswer.vue'
import QuizHeader from '@/components/QuizHeader.vue'
import Sound from '@/lib/sound.js'
import ClosePlayingButton from '@/components/ClosePlayingButton.vue'
import VideoPlayer from '@/components/VideoPlayer.vue'
import BgMusicMuteBtn from '@/components/BgMusicMuteBtn.vue'

import definite from '@/assets/sounds/definite.mp3'
import pop from '@/assets/sounds/pop.mp3'
import helperMixins from '@/mixins/helper.js'
import balanceText from '@/mixins/balance-text.js'
import setBackgroundQuestion from '@/mixins/set-bg-question.js'
import appConfig from '@/app.config.js'
import HotspotAnswer from '@/components/HotspotAnswer.vue'

export default {
    metaInfo() {
        return {
            link: [
                {
                    rel: 'canonical',
                    href: this.canonicalUrl
                }
            ]
        }
    },

    components: {
        Equaliser,
        ChoiceAnswer,
        TypingAnswer,
        Scramble,
        SortingAnswer,
        QuizHeader,
        ClosePlayingButton,
        VideoPlayer,
        BgMusicMuteBtn,
        HotspotAnswer
    },

    mixins: [preloadSongMixin, helperMixins, balanceText, setBackgroundQuestion],

    computed: {
        ...mapGetters({
            question: 'player/getCurrentQuiz',
            questionCount: 'player/getQuestionCount',
            audioSpotify: 'getSpotifyAudio'
        }),

        ...mapState({
            quiz: state => state.player.quiz,
            title: state => state.player.quiz.title,
            name: state => state.player.name,
            code: state => state.player.quiz.code,
            type: state => state.player.quiz.type,
            currentQuizIndex: state => state.player.currentQuizIndex,
            isMuted: state => state.isMuted,
            embedToken: state => state.player.embedToken
        }),

        isMultiple() {
            return this.question.answers_required === 'multiple'
        },

        canonicalUrl() {
            return `${process.env.VUE_APP_ROOT_URL}play/${this.$route.params.code}`
        }
    },

    created() {
        this.randomChoices()
        this.sound.registerSound({ id: 'definite', src: definite })
        this.sound.registerSound({ id: 'pop', src: pop })
    },

    mounted() {
        //from mixins set bg question//
        this.removeBackdrop()
        this.removeVideoBackdrop()

        if (this.isSupportBasicFullscreen(this.question)) {
            this.setTitleImageToBackground(this.question)
        }

        if (this.isSupportVideoFullscreen(this.question)) {
            this.setTitleImageToBackground(this.question)
            this.setVideoToBackground(this.question)
        }

        const contentElem = document.getElementsByClassName('content-container')[0]
        if (this.question.answers.length == 4 && this.isAllImageAnswer(this.question.answers)) {
            contentElem.classList.add('count-box-size')
        } else {
            contentElem.classList.remove('count-box-size')
        }

        this.touchActionNone()

        if (this.question.title) {
            this.useBalanceText('#question-title')
        }

        if (this.question.question_type == 'video' && this.$refs.videoPlayer) {
            this.$nextTick(() => {
                this.$refs.videoPlayer.setPlay()
            })
        }

        window.onbeforeunload = function () {
            return 'Are you sure you want to leave this page? You will loose any unsaved actions'
        }

        setTimeout(() => {
            this.resizeScaleQuizFrame()
        }, 100)

        this.checkTypeOfQuestion()
        this.setPuzzleImage()

        if (this.question && this.question.title) {
            helper.quizFontSize('question-title', this.question.title.length)
        }
    },

    destroyed() {
        window.onbeforeunload = function () {}
    },

    data() {
        return {
            selectedChoice: [],
            startTimer: new Date().getTime(),
            isSelected: false,
            isAlreadyShow: false,
            songXhrRequest: null,
            isSoundQuestion: false,
            isVideoQuestion: false,
            puzzleSize: null,
            sound: new Sound(),
            rawQuestion: {},
            correctIconList: [
                {
                    icon: 'fa-check-circle',
                    bg: true
                },
                {
                    icon: 'fa-laugh-wink',
                    bg: true
                },
                {
                    icon: 'fa-grin-stars',
                    bg: true
                },
                {
                    icon: 'fa-star',
                    bg: false
                },
                {
                    icon: 'fa-heart',
                    bg: false
                },
                {
                    icon: 'fa-thumbs-up',
                    bg: false
                }
            ]
        }
    },

    methods: {
        isAllImageAnswer: function (answers) {
            var hasImages = 0

            answers.forEach(function (answer) {
                if (answer.image) {
                    hasImages++
                }
            })

            return hasImages == answers.length
        },

        handleClickSelectAnswer: function (index) {
            if (this.question.question_type == 'video') {
                this.$refs.videoPlayer.stopVideo()
            }

            if (this.isSelected) {
                return false
            }

            if (this.isMultiple) {
                let indexValidation = 0
                let sameIndex = 0

                this.selectedChoice.map((selected, i) => {
                    if (selected === index) {
                        indexValidation++
                        sameIndex = i
                    }
                })

                if (indexValidation > 0) {
                    this.selectedChoice.splice(sameIndex, 1)
                } else {
                    this.selectedChoice.push(index)
                }

                if (this.selectedChoice.length !== this.question.answers_count) {
                    return false
                }
            } else {
                this.selectedChoice = [index]
            }

            this.isSelected = true

            this.$store.dispatch('player/handleNextResult')
            this.$store.commit('player/handleSetUserAnswer', {
                is_correct: this.checkIsCorrect(),
                timer: this.getTimer(),
                question_uid: this.question.uid,
                selected_index: this.isMultiple ? this.selectedChoice : index
            })

            let timeout = 0

            if (this.question.question_type == 'hotspot') {
                timeout = 500
            }

            setTimeout(() => {
                this.$router.replace({
                    name: 'play-alone-result',
                    params: {
                        code: this.$route.params.code,
                        is_correct: this.checkIsCorrect(),
                        choice_selected:
                            typeof this.selectedChoice !== 'object' ? [this.selectedChoice] : this.selectedChoice
                    }
                })
            }, timeout)

            if (isLoggedIn() && this.checkIsCorrect()) {
                this.$store.commit('player/addCoin')
            }

            if (!this.checkIsCorrect()) {
                this.$store.commit('player/pushWrongQuestion', this.rawQuestion)
            }

            if (!isNaN(this.audioSpotify.duration)) {
                this.audioSpotify.pause()
            }
        },

        submitTyping(value) {
            if (this.isSelected) {
                return false
            }

            if (isLoggedIn() && value.value) {
                this.$store.commit('player/addCoin')
            }

            if (!value.value) {
                this.$store.commit('player/pushWrongQuestion', this.rawQuestion)
            }

            this.isSelected = true

            this.$store.dispatch('player/handleNextResult')
            this.$store.commit('player/handleSetUserAnswer', {
                is_correct: value.value,
                timer: this.getTimer(),
                question_uid: this.question.uid,
                selected: value.sortingList || value.typingAnswer
            })

            this.selectedChoice = [0]

            this.$router.replace({
                name: 'play-alone-result',
                params: {
                    code: this.$route.params.code,
                    is_correct: value.value,
                    choice_selected:
                        typeof this.selectedChoice !== 'object' ? [this.selectedChoice] : this.selectedChoice
                }
            })

            if (!isNaN(this.audioSpotify.duration)) {
                this.audioSpotify.pause()
            }

            if (this.question.question_type == 'video') {
                this.$refs.videoPlayer.stopVideo()
            }
        },

        checkIsCorrect() {
            let validation = 0
            let selected = typeof this.selectedChoice !== 'object' ? [this.selectedChoice] : this.selectedChoice

            this.question.answers.forEach(answer => {
                selected.forEach(select => {
                    if (select === answer.initIndex && answer.is_correct) {
                        validation++
                    }
                })
            })

            return (
                (validation === this.question.answers_count && this.isMultiple) || (validation > 0 && !this.isMultiple)
            )
        },

        getTimer: function () {
            var current = new Date().getTime(),
                secondDiff = Math.abs(current - this.startTimer)

            return Math.ceil(secondDiff / 1000)
        },

        randomChoices: function () {
            this.question.answers = this.question.answers.map((item, index) => {
                return {
                    ...item,
                    initIndex: index
                }
            })

            this.rawQuestion = JSON.parse(JSON.stringify(this.question))
            this.question.answers = this.shuffle(this.question.answers)
        },

        shuffle: function (array) {
            let currentIndex = array.length,
                temporaryValue,
                randomIndex

            while (0 !== currentIndex) {
                randomIndex = Math.floor(Math.random() * currentIndex)
                currentIndex -= 1
                temporaryValue = array[currentIndex]
                array[currentIndex] = array[randomIndex]
                array[randomIndex] = temporaryValue
            }

            return array
        },

        resizeScaleQuizFrame() {
            let footerHeight
            const contentElem = document.getElementsByClassName('content-container')[0]
            const layoutElem = document.getElementsByClassName('layout')[0]
            const headerElem = document.getElementsByClassName('header')[0]
            const footerElem = document.getElementsByClassName('footer')[0]
            if (this.question.question_type == 'music') {
                footerHeight = footerElem.getBoundingClientRect().height + 100
            } else {
                footerHeight = footerElem.getBoundingClientRect().height
            }

            const headerHeight = headerElem.getBoundingClientRect().height
            const uselessHeight = headerHeight + footerHeight

            layoutElem.style.setProperty('--useless-h', `${uselessHeight}px`)

            new ResizeScale({
                width: contentElem.getBoundingClientRect().width,
                height: contentElem.getBoundingClientRect().height,
                child: contentElem,
                parent: layoutElem,
                uselessHeight: uselessHeight,
                noTransForm: true
            })

            document.getElementById('app').classList.add('overflow-hidden')
            document.getElementById('app').classList.add('relative')

            this.isAlreadyShow = true
        },

        playSong() {
            const track = this.question.spotify_track

            let audio = this.audioSpotify
            let cachePreviewUrl = track.cache_preview_url ? track.cache_preview_url : track.preview_url

            cachePreviewUrl = cachePreviewUrl.indexOf('mp3') > -1 ? cachePreviewUrl : `${cachePreviewUrl}.mp3`

            this.songXhrRequest = helper.ajaxBlob(
                cachePreviewUrl,
                res => {
                    audio.setAttribute('src', res)
                    audio.play()
                    audio.muted = false
                },
                () => {}
            )

            this.isSoundQuestion = true

            if (this.quiz.questions[this.currentQuizIndex + 1]) {
                this.preloadNextSong(this.quiz.questions[this.currentQuizIndex + 1])
            }
        },

        playRevealImage() {
            // Use css by add class animate-reveal instead, we can remove gsap function later
            // gsap.set('.bg-main', { filter: "blur(10px)" })
            // gsap.to('.bg-main', { filter: "blur(0px)", duration: 10, ease: 'none' })
        },

        checkTypeOfQuestion() {
            // Support old question type
            if (
                !this.question.question_type &&
                this.question.spotify_track &&
                this.question.spotify_track.preview_url
            ) {
                this.playSong()
            }

            switch (this.question.question_type) {
                case 'music':
                    this.playSong()
                    break

                case 'reveal_image':
                    this.playRevealImage()
                    break

                case 'scale_image':
                    this.playScaleImage()
                    break
            }
        },

        playScaleImage() {
            gsap.set('.bg-main', { backgroundSize: '500% 500%' })
            gsap.to('.bg-main', { backgroundSize: '100% 100%', duration: 10, ease: 'none' })
        },

        setPuzzleImage() {
            const layoutElem = document.getElementsByClassName('layout')[0]
            let horizontalPieces = Math.floor(Math.sqrt(this.question.puzzle_block))
            let verticalPieces = Math.ceil(Math.sqrt(this.question.puzzle_block))
            let pieces = {
                horizontalPieces,
                verticalPieces
            }

            if (!this.question.puzzle_block) {
                pieces = {
                    horizontalPieces: 2,
                    verticalPieces: 2
                }
            }

            if (!this.$refs['puzzle-container']) {
                return
            }

            this.puzzleSize = 500

            if (layoutElem.getBoundingClientRect().width < 1366) {
                this.puzzleSize =
                    layoutElem.getBoundingClientRect().width > 400 ? 400 : layoutElem.getBoundingClientRect().width
            }

            this.$refs['puzzle-container'].style.width = `calc(${this.puzzleSize}px - 2rem)`
            this.$refs['puzzle-container'].style.height = `calc(${this.puzzleSize}px - 2rem)`

            new Puzzle(
                {
                    container: this.$refs['puzzle-container'],
                    image: this.question.image,
                    snap: () => {
                        this.sound.play('pop')
                    },
                    onComplete: () => {
                        this.puzzleResultAnimation(true)
                        setTimeout(() => {
                            this.handleClickSelectAnswer(0)
                        }, appConfig.delayPuzzleDone * 1000)
                    }
                },
                pieces
            )
        },

        goBack() {
            this.$router.replace('/')
        },

        setFormatText: helper.setFormatText,

        puzzleResultAnimation(answer) {
            const bgIcon = document.querySelector('.circle-bg')

            if (answer) {
                const correctIcon = document.querySelector('#check-icon')
                const randomIcon = this.correctIconList[Math.floor(Math.random() * this.correctIconList.length)].icon
                correctIcon.classList.add(randomIcon)

                if (randomIcon.bg) {
                    bgIcon.classList.remove('hide')
                }

                correctIcon.classList.remove('hide')
            } else {
                bgIcon.classList.add('wrong')
                bgIcon.classList.remove('hide')
                document.querySelector('#x-icon').classList.remove('hide')
            }

            const resultTimeline = gsap.timeline({ delay: 0, repeat: 0 })

            gsap.set('.puzzle-backdrop', { opacity: 0 })
            gsap.set('.circle-box', { opacity: 0, y: '18.7rem' })

            resultTimeline
                .to('.puzzle-backdrop', { duration: 0.5, opacity: 1, display: 'block' }, 0)
                .to('.circle-box', { duration: 0.5, opacity: 0.75, y: 0, ease: 'back.out(1)' }, 0.3)
                .to('.circle-box', { duration: 0.5, opacity: 0, y: '-18.7rem' }, 1.3)
                .to('.puzzle-backdrop', { duration: 0.5, opacity: 0 }, 1.3)
        }
    },

    beforeRouteLeave(to, from, next) {
        window.onbeforeunload = function () {}

        if (this.audioSpotify) {
            this.audioSpotify.setAttribute('src', null)
        }

        if (this.songXhrRequest) {
            this.songXhrRequest.abort()
        }

        if (to.name === 'play-alone-result') {
            next()
        } else {
            next(false)
            popup.confirm(
                this.$t('playalone_alert_leave'),
                () => {
                    let { r } = this.$route.query

                    if (r && r === 'my-quiz') {
                        window.location.href = '/my-quiz'
                        return
                    }

                    if (r && r === 'play-alone') {
                        this.$router.replace('/my-quiz')
                        return
                    }

                    window.location.href = '/'
                },
                () => {
                    window.onbeforeunload = () => {
                        return 'Are you sure you want to leave this page? You will loose any unsaved actions'
                    }
                }
            )
        }
    }
}
</script>

<style lang="scss" scoped>
.border-red-light {
    border-color: var(--icon-color);
}

.logo {
    @media (min-width: 767px) {
        width: 12rem;
    }
}

.show-answer {
    opacity: 1 !important;
    pointer-events: all;
}
.layout {
    height: calc(100vh - 2rem);
    height: calc((var(--vh, 1vh) * 100) - 2rem);
    margin-top: -1rem;
    margin-right: -1rem;
    margin-left: -1rem;

    @media (max-width: 320px) {
        height: calc(100vh);
        height: calc((var(--vh, 1vh) * 100));
    }

    &.layout-scale {
        padding: 0;
    }

    .content {
        padding-left: 1rem;
        padding-right: 1rem;

        @media screen and (max-width: 767px) {
            overflow: hidden;
            padding-left: 0;
            padding-right: 0;
        }

        @media screen and (min-width: 515px) and (max-width: 740px) {
            padding: 0;
        }

        #question-title {
            word-break: break-word;
        }

        .video-box {
            width: 450px;
            aspect-ratio: 16 / 9;
            overflow: hidden;

            @media screen and (max-width: 500px) {
                min-width: unset;
                max-width: unset;
                width: 100%;
                height: auto;
                border: none;
                border-radius: 0;
            }
        }

        .answer-box {
            padding: 0 1rem;
        }

        .puzzle-backdrop {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.3);
            z-index: 2;
            display: none;
            overflow: hidden;

            .circle-box {
                position: absolute;
                top: calc(50% - (8rem / 2));
                left: calc(50% - (8rem / 2));
                width: 8rem;
                height: 8rem;

                .circle-bg {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    width: 5.4rem;
                    height: 5.4rem;
                    background-color: rgba(0, 0, 0, 0.3);

                    &.wrong {
                        background-color: #fff;
                    }
                }

                i {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    font-size: 8rem;
                    text-shadow: 0 0 4px #000;

                    &#check-icon {
                        color: #fff;
                    }

                    &.fa-times-circle {
                        color: red;
                    }
                }

                .hide {
                    opacity: 0;
                }
            }
        }
    }
}

@media screen and (min-width: 768px) and (max-width: 1920px) {
    section.layout .content {
        overflow: hidden;
        height: calc(100% - var(--useless-h));
    }
}

@media screen and (max-width: 767px) {
    section.layout .content {
        overflow: hidden;
        height: calc(100% - var(--useless-h));
    }
}

.bg-main-container::before {
    border: none;
}

.bg-main {
    &.animate-reveal {
        animation: reveal 10s none;
    }
}

@keyframes reveal {
    0% {
        filter: blur(10px);
    }

    100% {
        filter: blur(0);
    }
}

.bg-main-container {
    border-color: var(--ui-color) !important;
}

@media screen and (max-width: 500px) {
    .bg-main-container {
        &.has-video {
            width: 100%;
        }
    }
}

@media screen and (min-width: 741px) {
    .content-container {
        width: 720px;
    }
}

@media screen and (min-width: 741px) {
    .count-box-size {
        width: 520px;
    }
}

@media screen and (min-width: 320px) and (max-width: 420px) {
    #question-title {
        padding-left: 0.5rem;
        padding-right: 0.5rem;
    }
}
</style>
